"use client";

import React from "react";
import { Mail, ArrowRight, Loader, MessageSquare } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in duration-500">
        <div className="mb-4 flex size-12 items-center justify-center rounded-md bg-secondary/20 text-primary">
          <MessageSquare className="size-6" />
        </div>
        <h3 className="font-default mb-2 text-xl font-semibold text-foreground">
          {successMessage}
        </h3>
        <p className="mb-6 text-muted-foreground">
          Nous avons bien reçu votre demande. Un expert vous contactera sous peu.
        </p>
        <Button onClick={resetForm} variant="outline" className="rounded-md border-primary text-primary hover:bg-primary/5">
          Envoyer un autre message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid gap-5 sm:grid-cols-2">
        <FormField control={control} name="name" rules={{
        required: "Le nom est requis"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Votre Nom</FormLabel>
              <FormControl>
                <Input placeholder="Jean Tremblay" className="bg-transparent border-b border-x-0 border-t-0 border-border focus-visible:border-primary focus-visible:ring-0 px-0 text-foreground" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Le courriel est requis",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Veuillez entrer une adresse courriel valide"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Adresse Courriel</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jean@exemple.com" className="bg-transparent border-b border-x-0 border-t-0 border-border focus-visible:border-primary focus-visible:ring-0 px-0 text-foreground" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="subject" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Type de projet</FormLabel>
            <FormControl>
              <Input placeholder="Ex: Refinancement, Travailleur autonome, etc." className="bg-transparent border-b border-x-0 border-t-0 border-border focus-visible:border-primary focus-visible:ring-0 px-0 text-foreground" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Le message est requis"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Détails de votre situation</FormLabel>
            <FormControl>
              <textarea placeholder="Décrivez brièvement votre situation hypothécaire..." className="flex min-h-[120px] w-full resize-none bg-transparent px-0 py-2 text-base placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 border-b border-border focus-visible:border-primary transition-colors text-foreground" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {submitError}
        </div>}

      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} className="w-full rounded-md bg-primary py-6 text-base font-semibold text-primary-foreground hover:bg-primary/90 shadow-md transition-all">
          {isSubmitting ? <>
              <Loader className="mr-2 size-5 animate-spin" />
              Envoi en cours...
            </> : "Soumettre ma demande"}
        </Button>
      </div>
    </Form>;
}
export default function ParlonsDeVotreProjet() {
  return <section data-section-id="224719" id="parlons-de-votre-projet" className="bg-background text-foreground py-24 md:py-32">
      <div className="container mx-auto px-6 max-w-6xl">
        <div className="grid gap-16 lg:grid-cols-12 lg:gap-8">
          
          {}
          <div className="lg:col-span-7 xl:col-span-8">
            <Card className="border-t-4 border-t-primary border-x-0 border-b-0 shadow-xl shadow-primary/5 bg-card sm:p-4 rounded-md">
              <CardHeader className="pb-8">
                <CardTitle className="font-serif text-3xl md:text-4xl text-primary">
                  Parlons de votre projet
                </CardTitle>
                <CardDescription className="text-base text-muted-foreground mt-2 font-default">
                  Que votre dossier soit complexe ou que vous soyez refusé ailleurs, nous avons des solutions adaptées à votre réalité.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <FormProvider formKey="project_form_contact" formId="project_form_contact" sectionName="parlons-de-votre-projet" defaultValues={{
                name: "",
                email: "",
                subject: "",
                message: ""
              }} validationRules={{
                name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                subject: {
                  required: false
                },
                message: {
                  required: true
                }
              }} submitText="Soumettre ma demande" successMessage="Votre demande a été transmise avec succès !">
                  <ContactFormInner />
                </FormProvider>
              </CardContent>
            </Card>
          </div>

          {}
          <div className="lg:col-span-5 xl:col-span-4 flex flex-col justify-center space-y-12 lg:pl-10">
            <div>
              <h3 className="font-serif text-2xl font-semibold text-primary mb-6">
                Coordonnées Directes
              </h3>
              
              <div className="space-y-8">
                <div className="group">
                  <h4 className="text-xs font-bold tracking-widest text-muted-foreground uppercase mb-2 font-default">
                    Demandes Générales
                  </h4>
                  <Link to="mailto:info@hypothequesjennymartel.com" className="inline-flex items-center text-lg font-medium text-foreground hover:text-secondary transition-colors font-default">
                    <Mail className="mr-3 size-5 text-secondary" />
                    info@hypothequesjennymartel.com
                    <ArrowRight className="ml-2 size-4 opacity-0 -translate-x-2 transition-all group-hover:opacity-100 group-hover:translate-x-0" />
                  </Link>
                </div>

                <div className="group">
                  <h4 className="text-xs font-bold tracking-widest text-muted-foreground uppercase mb-2 font-default">
                    Urgence Hypothécaire
                  </h4>
                  <Link to="/ContactPage" className="inline-flex items-center text-lg font-medium text-foreground hover:text-secondary transition-colors font-default">
                    <MessageSquare className="mr-3 size-5 text-secondary" />
                    Consultation Rapide
                    <ArrowRight className="ml-2 size-4 opacity-0 -translate-x-2 transition-all group-hover:opacity-100 group-hover:translate-x-0" />
                  </Link>
                </div>
              </div>
            </div>

            <div className="rounded-md bg-muted p-6 border border-border/50">
              <p className="text-sm text-muted-foreground leading-relaxed font-default">
                Nous analysons chaque dossier avec rigueur. Notre spécialité réside dans l'accompagnement personnalisé pour les situations atypiques : travailleurs autonomes, crédit à rebâtir ou préavis de 60 jours.
              </p>
            </div>
          </div>

        </div>
      </div>
    </section>;
}